/**
 * TC dashboard v2 — Performance & Team tab model (TC-DASHBOARD-V2-DESIGN §4.3). Shape = the N11 contract for
 * GET /api/dashboard/performance; assembled today from the P&L, the operations reports, the client list and
 * the dashboard endpoints.
 */

import ApiService from '../api.service';
import { unwrapData } from '../../utils/apiConvert';
import { financeReportsService } from '../accounting/financeReports.service';
import { profitLossService } from '../accounting/profitLoss.service';
import { delta, marginPct } from '../../utils/dashboard/metrics';
import { previousRange, toIso } from '../../utils/dashboard/period';

const n = (v) => Number(v) || 0;
const failText = (err) => (err?.response?.status === 403 ? 'No access' : 'Could not load');

// Revenue and net profit per month of one annual P&L ({ 'YYYY-MM': { revenue, profit } })
const monthsOf = (annual) => {
  const out = {};
  if (!annual) return out;
  const revenue = (annual.sections || []).find((s) => s.key === 'revenue')?.totals?.months || [];
  const net = (annual.summary || []).find((l) => /^net profit$/i.test(l.name || ''))?.months || [];
  (annual.months || []).forEach((m, i) => { out[String(m).slice(0, 7)] = { revenue: n(revenue[i]), profit: n(net[i]) }; });
  return out;
};

// user: the signed-in user (its typing centre's client list)
export const loadPerformance = async (range, today = new Date(), mock = {}, user = null) => {
  const prev = previousRange(range);
  const year = today.getFullYear();
  const t = toIso(today);
  const in30 = toIso(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 30));
  const [plR, plPrevR, thisYearR, lastYearR, svcR, inactiveR, teamR, orgsR, expR, turnR, turnPrevR, clientsR, clientsPrevR, peopleR] = await Promise.allSettled([
    profitLossService.get({ from: range.from, to: range.to }),
    profitLossService.get({ from: prev.from, to: prev.to }),
    financeReportsService.getAnnualPl({ year }),
    financeReportsService.getAnnualPl({ year: year - 1 }),
    financeReportsService.getServiceProfit({ from: range.from, to: range.to }),
    financeReportsService.getInactiveClients({ days: 60 }),
    financeReportsService.getAgentPerformance({ from: range.from, to: range.to }),
    ApiService.getInternalOrganizations(user?.typingCenterId),
    ApiService.get('/api/document-expiry', { params: { page: 0, size: 1, fromDate: t, toDate: in30 } }),
    ApiService.get('/api/dashboard/turnaround', { params: range }),
    ApiService.get('/api/dashboard/turnaround', { params: prev }),
    ApiService.get('/api/dashboard/invoiced-clients', { params: range }),
    ApiService.get('/api/dashboard/invoiced-clients', { params: prev }),
    ApiService.get('/api/dashboard/people', { params: range }),
  ]);
  const errors = {};
  const val = (r, card) => { if (r.status === 'fulfilled') return r.value; errors[card] = failText(r.reason); return null; };

  const pl = val(plR, 'revenue');
  const plPrev = plPrevR.status === 'fulfilled' ? plPrevR.value : null;
  const rev = n(pl?.revenue?.total);
  const revPrev = plPrev ? n(plPrev.revenue?.total) : null;
  const margin = marginPct(n(pl?.netProfit), rev);
  const marginPrev = plPrev ? marginPct(n(plPrev.netProfit), revPrev) : null;

  // 12 months ending with the current one, from this year's and last year's annual P&L
  const byMonth = { ...monthsOf(val(lastYearR, 'trend')), ...monthsOf(val(thisYearR, 'trend')) };
  const trend = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(today.getFullYear(), today.getMonth() - 11 + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    return { month: key, revenue: byMonth[key]?.revenue || 0, profit: byMonth[key]?.profit || 0 };
  });

  const turn = turnR.status === 'fulfilled' ? unwrapData(turnR.value) : null;
  if (!turn) errors.turnaround = failText(turnR.reason);
  const turnPrev = turnPrevR.status === 'fulfilled' ? unwrapData(turnPrevR.value) : null;
  const turnDays = turn?.averageDays == null ? null : n(turn.averageDays);
  const turnPrevDays = turnPrev?.averageDays == null ? null : n(turnPrev.averageDays);
  const turnSeries = (turn?.series || []).filter((b) => b.averageDays != null).map((b) => n(b.averageDays));

  const clients = clientsR.status === 'fulfilled' ? n(unwrapData(clientsR.value)?.count) : null;
  if (clients == null) errors.revenuePerClient = failText(clientsR.reason);
  const clientsPrev = clientsPrevR.status === 'fulfilled' ? n(unwrapData(clientsPrevR.value)?.count) : null;
  const perClient = clients ? rev / clients : null;
  const perClientPrev = clientsPrev && revPrev != null ? revPrev / clientsPrev : null;

  const orgs = val(orgsR, 'centre');
  const people = peopleR.status === 'fulfilled' ? unwrapData(peopleR.value) : null;
  if (!people) errors.people = failText(peopleR.reason);
  const orgList = Array.isArray(orgs?.data?.data) ? orgs.data.data : [];
  const monthStart = toIso(new Date(today.getFullYear(), today.getMonth(), 1));
  const exp = expR.status === 'fulfilled' ? (expR.value?.data?.data || {}) : {};

  return {
    centre: {
      clients: orgList.length,
      newClients: orgList.filter((o) => String(o.createdAt || o.created_at || '').slice(0, 10) >= monthStart).length,
      employees: n(people?.employees), newEmployees: n(people?.newEmployees),
      documents: n(people?.documents), newDocuments: n(people?.newDocuments),
      peopleFailed: !people,
      expiring30: n(exp.totalElements ?? exp.total_elements),
    },
    revenue: { amount: rev, delta: delta(rev, revPrev) },
    margin: { pct: margin, deltaPts: margin != null && marginPrev != null ? Math.round(margin - marginPrev) : null },
    revenuePerClient: { amount: perClient, delta: perClient == null ? null : delta(perClient, perClientPrev) },
    turnaround: { days: turnDays, deltaDays: turnDays != null && turnPrevDays != null ? Math.round((turnDays - turnPrevDays) * 10) / 10 : null, series: turnSeries },
    trend,
    callList: ((val(inactiveR, 'callList')?.rows) || []).map((r) => ({ customerId: r.customerId, name: r.customer, daysInactive: r.daysInactive == null ? null : n(r.daysInactive), walletBalance: n(r.walletBalance) }))
      .sort((a, b) => (b.daysInactive ?? Infinity) - (a.daysInactive ?? Infinity)).slice(0, 3),
    services: ((val(svcR, 'services')?.rows) || []).map((r) => ({ name: r.service, profit: n(r.profit), marginPct: n(r.marginPercent) }))
      .sort((a, b) => b.profit - a.profit).slice(0, 5),
    team: ((val(teamR, 'team')?.rows) || []).map((r) => ({ userId: r.userId, name: r.name, wos: n(r.tasksCompleted), revenue: n(r.revenue), commission: n(r.commission) }))
      .sort((a, b) => b.revenue - a.revenue),
    errors,
  };
};
